<ng-container *transloco="let t">
  <!-- Documents header -->
  <div class="flex items-center justify-between">
    <h2 class="text-xl font-semibold text-ink-heading">{{ t('FILE.DOCUMENTS') }}</h2>
    <span
      class="flex size-6 items-center justify-center rounded-full bg-primary-tint text-[0.6875rem] font-medium text-primary"
    >
      {{ uploadedFiles.length | translocoDecimal : { minimumIntegerDigits: 2 } }}
    </span>
  </div>

  <hr class="my-4 border-0 border-t border-stroke-soft" />

  <!-- Empty state icon (above dropzone, when no files) -->
  @if (uploadedFiles.length === 0) {
  <div class="flex flex-col items-center justify-center py-4 gap-2 text-ink-muted">
    <div class="flex items-center justify-center shrink-0 size-16 rounded-full">
      <mat-icon svgIcon="file-outline" class="size-10!"></mat-icon>
    </div>
    <p class="text-sm font-semibold text-ink-muted">{{ t('FILE.NO_DOCUMENTS') }}</p>
  </div>
  }

  <!-- Upload dropzone -->
  <div
    (fileDropped)="handleFile($event)"
    [allowedFileExtensions]="['pdf']"
    dssLibsUiDragAndDrop
    class="upload-dropzone"
    [class.upload-dropzone--error]="dropzoneHasError()"
    [class.upload-dropzone--filled]="uploadedFiles.length > 0"
  >
    <label class="flex min-h-44 flex-col items-center justify-center gap-3 cursor-pointer p-6">
      <!-- Upload icon -->
      <div class="flex items-center justify-center size-7 rounded bg-primary-tint">
        <mat-icon class="size-7 text-primary!" svgIcon="cloud-upload-outline"> </mat-icon>
      </div>
      <span class="text-xs font-medium text-ink text-center hidden lg:block">
        {{ t('FILE.DROP_FILE') }}
        <span class="text-primary font-bold underline">{{ t('FILE.BROWSE') }}</span>
      </span>
      <span class="text-xs font-medium text-ink lg:hidden">{{ t('FILE.UPLOAD_DOCUMENTS') }}</span>
      <input
        #fileInput
        (change)="handleFile($event.target)"
        accept=".pdf"
        class="hidden"
        multiple
        required
        type="file"
      />
    </label>
  </div>

  <!-- Dropzone error message -->
  @if (dropzoneHasError()) {
  <div class="flex items-center gap-2 mt-3 text-danger">
    <mat-icon class="size-4! shrink-0" svgIcon="danger-triangle-bold"></mat-icon>
    <p class="text-xs">{{ t('VALIDATION_MESSAGES.DOCUMENT_REQUIRED') }}</p>
  </div>
  }

  <!-- File list -->
  @if (uploadedFiles.length > 0) {
  <div class="mt-4 flex flex-col gap-2">
    @for (file of uploadedFiles; track $index) {
    <div class="flex items-center gap-1.5 py-2">
      <!-- File icon -->
      <div class="flex items-center justify-center size-8 rounded bg-primary-tint">
        <mat-icon class="size-5! shrink-0 text-primary!" svgIcon="file-bold"></mat-icon>
      </div>
      <!-- File info -->
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-ink truncate" [title]="file.FileName">
          {{ file.FileName | truncate : 30 : true }}
        </p>
        <p class="text-xs text-ink-muted">{{ file.FileSizeKb | fileSize }}</p>
      </div>

      <!-- Trash button -->
      <button
        type="button"
        (click)="removeFile($index, file.FileName)"
        (keydown.enter)="removeFile($index, file.FileName)"
        class="text-danger! hover:text-danger-hover transition-colors flex items-center justify-center leading-none shrink-0 cursor-pointer"
      >
        <mat-icon class="size-5! cursor-pointer" svgIcon="trash-bin-minimalistic-outline"></mat-icon>
      </button>
    </div>
    }
  </div>
  }

  <!-- Rename modal for duplicate files -->
  @if (isRenameModalOpen) {
  <ng-template #renameHeaderTpl>
    <ng-container *transloco="let t">
      <div class="p-2 rounded-lg bg-primary-soft">
        <div class="flex items-center justify-center size-6 shrink-0 text-primary">
          <mat-icon svgIcon="file-outline" />
        </div>
      </div>
      <div class="flex flex-col gap-0.5">
        <h2 class="font-semibold text-base text-ink-strong">{{ t('FILE.RENAME_TITLE') }}</h2>
        <p class="text-xs text-ink-tertiary">{{ t('FILE.RENAME_SUBTITLE') }}</p>
      </div>
    </ng-container>
  </ng-template>

  <ng-template #renameActionsTemplate>
    <div class="flex items-center justify-end gap-3">
      <dss-libs-ui-button color="gray" variant="outline" label="GENERAL.CANCEL" (clickOutput)="clearModalData()" />
      <dss-libs-ui-button color="primary" label="SAVE" (clickOutput)="closeModal(newFileNames)" />
    </div>
  </ng-template>

  <div class="fixed inset-0 z-20 flex justify-center items-center cdk-overlay-backdrop">
    <div class="w-96 bg-white rounded-xl overflow-hidden shadow-[0px_8px_32px_rgba(0,0,0,0.15)]">
      <dss-libs-ui-base-modal [header]="renameHeaderTpl" [actions]="renameActionsTemplate" (closed)="clearModalData()">
        <div class="flex flex-col gap-4 px-6 py-4" *transloco="let t">
          @for (file of duplicateFiles; track file) {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-ink-strong" [for]="'rename-file-' + $index">{{ file.name }} </label>
            <input
              [(ngModel)]="newFileNames[$index]"
              class="form-input focus:border-primary"
              [id]="'rename-file-' + $index"
              type="text"
            />
            @if (!isFileNameUnique(newFileNames[$index])) {
            <span class="text-xs text-danger">{{ t('FILE.DUPLICATE_ERROR_MESSAGE') }}</span>
            }
          </div>
          }
        </div>
      </dss-libs-ui-base-modal>
    </div>
  </div>
  }
</ng-container>
